import React, { useEffect, useRef, useState } from 'react';
import { easeOutCubic } from './powerMapLayout';
import { InfluenceArea } from './influenceModel';
import { areaColor } from './ui';

interface RadarProps {
  areas: InfluenceArea[];
  /** areaId → score (missing = 0) */
  values: Record<string, number>;
  /** Proposed scores drawn as a dashed outline */
  proposal?: Record<string, number> | null;
  size?: number;
  reducedMotion?: boolean;
  label: string;
}

/** Tweens a record of numbers towards its target */
function useTweened(target: Record<string, number>, keys: string[], reduced: boolean, duration = 800) {
  const [shown, setShown] = useState<Record<string, number>>(() => Object.fromEntries(keys.map((k) => [k, 0])));
  const fromRef = useRef(shown);
  fromRef.current = shown;
  const signature = keys.map((k) => `${k}:${target[k] ?? 0}`).join('|');

  useEffect(() => {
    const goal = Object.fromEntries(keys.map((k) => [k, target[k] ?? 0]));
    if (reduced || typeof window.requestAnimationFrame !== 'function') {
      setShown(goal);
      return;
    }
    const from = { ...fromRef.current };
    const start = performance.now();
    let frame = 0;
    const step = (now: number) => {
      const t = Math.min(1, (now - start) / duration);
      const e = easeOutCubic(t);
      setShown(Object.fromEntries(keys.map((k) => [k, (from[k] ?? 0) + (goal[k] - (from[k] ?? 0)) * e])));
      if (t < 1) frame = window.requestAnimationFrame(step);
    };
    frame = window.requestAnimationFrame(step);
    return () => window.cancelAnimationFrame(frame);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature, reduced]);

  return shown;
}

/** Compact polar chart of an entity's area scores; morphs when scores change */
export const InfluenceRadar: React.FC<RadarProps> = ({ areas, values, proposal, size = 232, reducedMotion = false, label }) => {
  const keys = areas.map((a) => a.id);
  const shown = useTweened(values, keys, reducedMotion);
  const shownProposal = useTweened(proposal || {}, keys, reducedMotion, 600);
  const n = areas.length;
  if (n < 3) return null;

  const pad = 44;
  // extra width so side labels ("Media/Information") never clip
  const side = 56;
  const width = size + side * 2;
  const cx = width / 2;
  const cy = size / 2;
  const R = size / 2 - pad;
  const angle = (i: number) => -Math.PI / 2 + (i * 2 * Math.PI) / n;
  const point = (i: number, v: number) => {
    const r = (Math.max(0, Math.min(100, v)) / 100) * R;
    return [cx + Math.cos(angle(i)) * r, cy + Math.sin(angle(i)) * r] as const;
  };
  const polygon = (vals: Record<string, number>) => areas.map((a, i) => point(i, vals[a.id] || 0).join(',')).join(' ');

  return (
    <svg width="100%" viewBox={`0 0 ${width} ${size}`} className="mx-auto block max-w-[320px]" role="img" aria-label={label}>
      {[25, 50, 75, 100].map((ring) => (
        <polygon
          key={ring}
          points={areas.map((_, i) => point(i, ring).join(',')).join(' ')}
          fill={ring === 100 ? '#fafafa' : 'none'}
          stroke="rgba(10,10,10,0.08)"
          strokeWidth={1}
          strokeDasharray={ring === 100 ? undefined : '2 3'}
        />
      ))}
      {areas.map((a, i) => {
        const [x, y] = point(i, 100);
        return <line key={a.id} x1={cx} y1={cy} x2={x} y2={y} stroke="rgba(10,10,10,0.07)" />;
      })}

      {proposal && (
        <polygon points={polygon(shownProposal)} fill="none" stroke="#0a0a0a" strokeOpacity={0.55} strokeWidth={1.25} strokeDasharray="4 3" strokeLinejoin="round" />
      )}
      <polygon points={polygon(shown)} fill="rgba(10,10,10,0.06)" stroke="#0a0a0a" strokeOpacity={0.8} strokeWidth={1.25} strokeLinejoin="round" />

      {areas.map((a, i) => {
        const v = shown[a.id] || 0;
        const [x, y] = point(i, v);
        const [lx, ly] = point(i, 100);
        const cos = Math.cos(angle(i));
        const sin = Math.sin(angle(i));
        const anchor = Math.abs(cos) < 0.3 ? 'middle' : cos > 0 ? 'start' : 'end';
        const tx = lx + cos * 9;
        // name then value, stacked away from the chart
        const ty = sin < -0.3 ? ly - 22 : sin > 0.3 ? ly + 19 : ly - 2;
        const scored = (values[a.id] || 0) > 0;
        return (
          <g key={a.id}>
            {scored && <circle cx={x} cy={y} r={3.25} fill={areaColor(a.color)} stroke="#fff" strokeWidth={1.25} />}
            <text x={tx} y={ty} textAnchor={anchor} className="text-[10.5px]" fill={scored ? '#525252' : '#a3a3a3'}>
              {a.label.length > 18 ? `${a.label.slice(0, 17)}…` : a.label}
            </text>
            <text x={tx} y={ty + 12} textAnchor={anchor} className="text-[10.5px] tabular-nums" fill={scored ? '#0a0a0a' : '#d4d4d4'}>
              {scored ? Math.round(v) : '—'}
            </text>
          </g>
        );
      })}
    </svg>
  );
};
